import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createHostFamilyLecComment } from '../../../../../api/host-families/host-family-lec-comments/create-host-family-lec-comment'
import { deleteHostFamilyLecComments } from '../../../../../api/host-families/host-family-lec-comments/delete-host-family-lec-comment'
import { getHostFamilyLecCommentGuests } from '../../../../../api/host-families/host-family-lec-comments/get-host-family-lec-comment-guests'
import { getHostFamilyLecComments } from '../../../../../api/host-families/host-family-lec-comments/get-host-family-lec-comments'
import { updateHostFamilyLecComment } from '../../../../../api/host-families/host-family-lec-comments/update-host-family-lec-comment'
import { useModalManager } from '../../../../../lib/hooks/use-modal-manager'
import { HostFamilyLecComment } from '../../../../../lib/types/models/host-family-lec-comment/host-family-lec-comment'
import { modalButtonConfig } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../../elements/action-table-wrapper'
import HostFamilyLecCommentForm from './host-family-lec-comment-form'
import {
  formatHostFamilyLecCommentGuest,
  getHostFamilyLecCommentGuestKey,
  mapHostFamilyLecCommentInputToRequest,
  mapHostFamilyLecCommentToInput
} from './utils'

type Props = {
  id: number
}

const HostFamilyLecCommentTable = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    HostFamilyLecComment
  >(['create', 'edit', 'delete'])

  const { data: hostFamilyLecComments } = useQuery({
    queryKey: ['host-family-lec-comments', id],
    queryFn: () => getHostFamilyLecComments({ hostFamilyId: id })
  })

  const { data: guests } = useQuery({
    queryKey: ['host-family-lec-comment-guests', id],
    queryFn: () => getHostFamilyLecCommentGuests(id)
  })

  const { mutateAsync: deleteComment, isPending: isLoading } = useMutation({
    mutationFn: deleteHostFamilyLecComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('delete')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createHostFamilyLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('create')
    }
  })

  const { mutateAsync: updateComment } = useMutation({
    mutationFn: updateHostFamilyLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<HostFamilyLecComment>[] = [
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 px-3 py-4">
          {row.original.guests.map((guest) => (
            <Typography
              key={getHostFamilyLecCommentGuestKey(guest)}
              variant="tag"
              color={tailwindColors.primary.DEFAULT}
              className="text-wrap"
            >
              {formatHostFamilyLecCommentGuest(guest)}
            </Typography>
          ))}
          <Typography variant="tag" color={tailwindColors.black.DEFAULT} className="text-wrap">
            {row.original.comment}
          </Typography>
        </div>
      )
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedBy',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {toFullName(row.original.commentedBy.firstName, row.original.commentedBy.lastName)}
        </Typography>
      )
    },

    {
      enableSorting: true,
      header: 'Visibilité',
      accessorKey: 'isVisibleForTheYear',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {row.original.isVisibleForTheYear ? 'Année en cours' : 'Indéfiniment'}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <Box className="bg-white-hover rounded-lg border border-grey-light p-0">
      <ActionTableWrapper
        title={'Commentaires LEC'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter un commentaire'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<HostFamilyLecComment>
          manualSorting={false}
          columns={columns}
          data={hostFamilyLecComments?.data ?? []}
          rowCount={hostFamilyLecComments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Écrire un commentaire'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <HostFamilyLecCommentForm
            guests={guests ?? []}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                await mutateAsync({
                  ...mapHostFamilyLecCommentInputToRequest(values),
                  hostFamilyId: id
                })
              }
            })}
            defaultValues={{
              isVisibleForTheYear: false,
              sharedToOl: false,
              guests: []
            }}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Modifier un commentaire'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <HostFamilyLecCommentForm
              // Guests linked earlier stay selectable even if they are no longer hosted this year
              guests={[
                ...selectedData.guests,
                ...(guests ?? []).filter(
                  (guest) =>
                    !selectedData.guests.some(
                      (linked) =>
                        getHostFamilyLecCommentGuestKey(linked) ===
                        getHostFamilyLecCommentGuestKey(guest)
                    )
                )
              ]}
              defaultValues={mapHostFamilyLecCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  await updateComment({
                    ...mapHostFamilyLecCommentInputToRequest(values),
                    hostFamilyId: id,
                    id: selectedData.id
                  })
                }
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                await deleteComment({ hostFamilyId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Supprimer un commentaire'}
            icon={DeleteIcon}
            isLoading={isLoading}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </Box>
  )
}

export default HostFamilyLecCommentTable
